// Wire shapes for Sample SubContract Tracking → Subout Registration (nav
// `suboutreg`). Mirrors the C# DTOs in
// AlpacaIntelligence.Application/SuboutRegistration/SuboutRegistrationModels.cs.

/** One sample+test+method in the Subout Registration queue. */
export interface SuboutRegistrationRow {
  sampleOid:      string;
  testMethodOid:  string;
  sampleId:       string | null;
  jobId:          string | null;
  sampleName:     string | null;
  sampleMatrix:   string | null;
  test:           string | null;
  method:         string | null;
  parameter:      string | null;   // ';'-joined analyte list for the group
  dueDate:        string | null;
  dateReceived:   string | null;
  client:         string | null;
  status:         string | null;   // "Pending Subcontract" … "Validated"
  statusId:       string | null;   // "SS-1-1" … "SS-1-6"
  suboutOrderId:  string | null;   // the order the row is in, null while pending
  isPending:      boolean;         // can be registered (SuboutSample IS NULL)
  parameterCount: number;
}

/** A lookup option for the registration dialog. */
export interface SuboutLookupItem {
  id:   string;
  name: string | null;
}

/** A contract lab + its auto-fill detail (Contract Lab Info section). */
export interface SuboutLabItem {
  id:              string;
  name:            string | null;
  accreditationId: string | null;
  contact:         string | null;
  phone:           string | null;
  email:           string | null;
  address:         string | null;
}

/** The lists the detail view needs. */
export interface SuboutLookups {
  contractLabs:     SuboutLabItem[];
  turnAroundTimes:  SuboutLookupItem[];
  preservatives:    SuboutLookupItem[];
  deliveryServices: SuboutLookupItem[];
}

/** One selected sample in the detail view's "Subout Samples" grid. */
export interface SuboutSampleDetailRow {
  sampleOid:       string;
  testMethodOid:   string;
  jobId:           string | null;
  sampleId:        string | null;
  matrix:          string | null;
  test:            string | null;
  method:          string | null;
  parameter:       string | null;
  containerType:   string | null;
  holdTime:        string | null;
  dateCollected:   string | null;
  originalDueDate: string | null;
  client:          string | null;
}

/** A (sample, test) pair — the grain the queue groups to and that New registers. */
export interface SuboutRegistrationKey {
  sampleOid:     string;
  testMethodOid: string;
}

/** New → register: the selected pending tests plus the order details. */
export interface RegisterSuboutRequest {
  items:              SuboutRegistrationKey[];
  contractLabOid:     string;
  turnAroundTimeOid?: string | null;
  numberOfBottles?:   number | null;
  preservativeOid?:   string | null;
  deliveryServiceOid?: string | null;
  trackingNo?:        string | null;
  remark?:            string | null;
  // Full-page detail view fields:
  dateShipped?:          string | null;
  dueDate?:              string | null;
  labAddress?:           string | null;
  contact?:              string | null;
  contactPhone?:         string | null;
  deliveryContact?:      string | null;
  deliveryContactPhone?: string | null;
  deliveryEmail?:        string | null;
}

/** One (sample, test) placed into the new order. */
export interface SuboutRegistrationChange {
  sampleOid:      string;
  testMethodOid:  string;
  sampleId:       string | null;
  test:           string | null;
  suboutOrderId:  string | null;
  parameterCount: number;
}

export interface RegisterResult {
  orderId:    string | null;
  registered: number;
  rows:       SuboutRegistrationChange[];
}

/** Stable grid row key — (sample, test) is the grain. */
export const rowKey = (r: SuboutRegistrationRow): string => `${r.sampleOid}:${r.testMethodOid}`;

// ── Subout Test Order — Edit / Delete / Submit ──────────────────────────────

/** The order header loaded into the Edit detail view. */
export interface SuboutTestOrderDetail {
  id:                   string | null;
  suboutOrderId:        string | null;
  dateRegistered:       string | null;
  registeredBy:         string | null;
  contractLabOid:       string | null;
  contractLabName:      string | null;
  accreditationId:      string | null;
  contact:              string | null;
  contactPhone:         string | null;
  contactEmail:         string | null;
  labAddress:           string | null;
  deliveryServiceOid:   string | null;
  trackingNo:           string | null;
  dateShipped:          string | null;
  deliveryContact:      string | null;
  deliveryContactPhone: string | null;
  deliveryEmail:        string | null;
  turnAroundTimeOid:    string | null;
  numberOfBottles:      number | null;
  preservativeOid:      string | null;
  remark:               string | null;
  dueDate:              string | null;
  statusText:           string | null;
  statusId:             string | null;
  suboutStatus:         number;
}

/** Edit → Save payload (the editable header fields). */
export interface UpdateTestOrderRequest {
  contractLabOid:        string;
  turnAroundTimeOid?:    string | null;
  numberOfBottles?:      number | null;
  preservativeOid?:      string | null;
  deliveryServiceOid?:   string | null;
  trackingNo?:           string | null;
  remark?:               string | null;
  dateShipped?:          string | null;
  labAddress?:           string | null;
  contact?:              string | null;
  contactPhone?:         string | null;
  deliveryContact?:      string | null;
  deliveryContactPhone?: string | null;
  deliveryEmail?:        string | null;
}

/** Per-order outcome of a bulk Submit / Delete. */
export interface SuboutTestOrderActionResult {
  id:            string | null;
  suboutOrderId: string | null;
  outcome:       string | null;
  ok:            boolean;
}
